<template>
    <div class="marketing-link g-clearfix">
        <WxOpenBrowserMask v-model="showMask" />
        <div
            class="marketing-link_button"
            :style="customStyle"
            @click="handleClick"
        >
            {{ text }}
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { isWx } from '@/util/report/nioapp-event-report/utils.js';
import { pushHash } from '@/util/dmp/push-hash';
import {
    isInNioCNApp,
    getLoginInfoBridge,
    redirectToViewBridge,
    applyJoinGroupBridge
} from '@/util/platform/universal'; // @/util/jsbridge/index
import { NIO_SCHEMA, DOWNLOAD_APP_URL } from '@/config/enums';
import {
    MARKETING_JOIN_CHAT_GROUP_BUSINESS_CODE,
    JOIN_CHAT_GROUP_TRACK_LINK,
    JOIN_CHAT_GROUP,
    DMP_LINK_TYPE_MAP
} from '../config';
import trackExposeMixin from '../mixin/track-expose';
import WxOpenBrowserMask from './wx-open-browser-mask';
import { asyncCopyTextToClipboard } from '@/util/index.js';

const mixin = {
    methods: {
        ...mapActions('marketing/', ['encodeGroupChatPassword'])
    }
};

export default {
    name: 'marketing-link',
    componentName: 'marketingLink',
    components: { WxOpenBrowserMask },
    props: {
        customStyle: String,
        text: String,
        href: String,
        trackEvent: Function,
        index: Number,
        rootMark: String,
        targetConfig: {
            type: Object,
            default: () => ({})
        },
        trackSelectAction: Function
    },
    mixins: [mixin, trackExposeMixin],
    data() {
        return {
            showMask: false
        };
    },
    beforeMount() {
        this.addTrackParams(this.trackExposeCustom);
    },
    methods: {
        async handleClick() {
            const { targetType, defaultURL = '' } = this.targetConfig;

            // 加入群聊
            if (targetType === JOIN_CHAT_GROUP) {
                this.handleJoinChatGroup();
                return;
            }

            if (!this.href.startsWith('http') && isWx()) {
                this.leadToBrowser();
                return;
            }

            await this.handleTrackClick(this.href);

            if (!this.href) {
                return;
            }
            if (!this.href.startsWith('http') && isInNioCNApp()) {
                redirectToViewBridge(this.href);
            } else {
                location.href = this.href;
                if (!this.href.startsWith('http') && !isInNioCNApp()) {
                    // 如果没有唤起app则3秒后会跳转到下载地址，反之则不会跳转到下载地址。
                    setTimeout(() => {
                        window.location.href = defaultURL || DOWNLOAD_APP_URL; //下载地址
                    }, 3000);
                }
            }
        },
        async handleJoinChatGroup() {
            const {
                offAppHref = '',
                groupId,
                inviterId,
                needToGenerateGroupPassword = false
            } = this.targetConfig;

            if (isInNioCNApp()) {
                const { data } = await getLoginInfoBridge();
                const isLogin = !!data.access_token;

                // 未登录：跳转登录页
                if (!isLogin) {
                    await this.handleTrackClick(NIO_SCHEMA.LOGIN);
                    redirectToViewBridge(NIO_SCHEMA.LOGIN);
                    return;
                }

                // 已登陆：≥ 5.18.0 调用bridge加入群聊; < 5.18.0 跳转到加入im群聊页面
                try {
                    const res = await applyJoinGroupBridge(groupId, inviterId);
                    if (res.result_code !== 'success') {
                        throw res;
                    }
                    await this.handleTrackClick(JOIN_CHAT_GROUP_TRACK_LINK);
                } catch (e) {
                    console.log('[applyJoinGroup] call bridge failed ', e);

                    const joinChatGroupScheme = `${NIO_SCHEMA.JOIN_CHAT_GROUP}?group_id=${groupId}&inviter_id=${inviterId}`;

                    await this.handleTrackClick(joinChatGroupScheme);
                    redirectToViewBridge(joinChatGroupScheme);
                }
            } else {
                if (!offAppHref.startsWith('http') && isWx()) {
                    this.leadToBrowser();
                    return;
                }

                const link = offAppHref || DOWNLOAD_APP_URL;

                await this.handleTrackClick(link);

                if (needToGenerateGroupPassword) {
                    // 生成入群口令 -> 写入剪切板
                    console.time('[COPY]');
                    const fetchPassword$ = this.encodeGroupChatPassword({
                        inviter_account_id: inviterId,
                        group_id: groupId,
                        business_code: MARKETING_JOIN_CHAT_GROUP_BUSINESS_CODE // 固定业务code
                    }).then(res => {
                        if (res.result_code !== 'success') {
                            throw res.message || 'request_failed';
                        }
                        return res.data;
                    });

                    asyncCopyTextToClipboard(fetchPassword$)
                        .then(() => {
                            this.$message('已复制进群码，请打开蔚来App');
                        })
                        .catch(e => {
                            console.warn(`[COPY FAILED]: ${e}`);
                            this.$message('进群码复制失败，请使用蔚来App扫码');
                        })
                        .finally(() => {
                            console.timeEnd('[COPY]');

                            setTimeout(() => {
                                location.href = link;
                            }, 1500);
                        });
                } else {
                    location.href = link;
                }
            }
        },
        async handleTrackClick(href) {
            const custom = this.addTrackParams();

            if (this.trackEvent) {
                await this.trackEvent({
                    click_url: href,
                    ...custom
                });
            }
            if (this.trackSelectAction) {
                await this.trackSelectAction({
                    click_url: href,
                    ...custom
                });
            }
        },
        addTrackParams(target = {}) {
            const { targetType, targetSubType, groupId, inviterId } =
                this.targetConfig || {};
            const { name: typeName = '', subTypes = {} } =
                DMP_LINK_TYPE_MAP[targetType] || {};
            const subTypeName = subTypes[targetSubType] || '';

            target.link_cate = typeName;
            target.link_target = subTypeName;
            // 按钮类型为加入群聊时，上报群聊ID和入群邀请人ID
            if (targetType === JOIN_CHAT_GROUP) {
                target.group_id = groupId;
                target.invite_account = inviterId;
            }
            return target;
        },
        leadToBrowser() {
            pushHash(
                encodeURIComponent(
                    JSON.stringify({
                        jump: 'immediate',
                        index: this.index,
                        from: this.rootMark
                    })
                )
            );
            this.showMask = true;
        }
    }
};
</script>

<style lang="less" scoped>
@import '~src/assets/css/mixin.less';
@import '~src/assets/css/variable.less';

.marketing-link {
    font-family: @font-family-base;
    background: #ffffff;

    &_button {
        .center();
        height: 45 * @layout375;
        font-size: 16 * @layout375;
        color: #ffffff;
        background: #00bcbc;
        border-radius: 25 * @layout375;
        position: relative;
    }
}
</style>




// WEBPACK FOOTER //
// src/views/marketing/components/link.vue